import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Plus, Edit, Trash2, Loader2, List, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Define all dropdown categories managed by this page
const DROPDOWN_CATEGORIES = [
  { id: 'referral_sources', name: 'Referral Sources', description: 'Where participants are referred from' },
  { id: 'participant_statuses', name: 'Participant Statuses', description: 'Status options for participants' },
  { id: 'employment_types', name: 'Employment Types', description: 'Types of employment' },
  { id: 'education_levels', name: 'Education Levels', description: 'Education level options' },
  { id: 'income_sources', name: 'Income Sources', description: 'Sources of income' },
  { id: 'housing_types', name: 'Housing Types', description: 'Housing situation options' },
  { id: 'disability_types', name: 'Disability Types', description: 'Types of disabilities' },
  { id: 'barrier_types', name: 'Employment Barriers', description: 'Barriers to employment' },
  { id: 'document_types', name: 'Document Types', description: 'Types of documents uploaded' },
  { id: 'appointment_types', name: 'Appointment Types', description: 'Types of appointments' },
  { id: 'communication_types', name: 'Communication Types', description: 'Types of communications' },
  { id: 'note_categories', name: 'Note Categories', description: 'Categories for case notes' },
  { id: 'tags', name: 'Tags', description: 'General tags for records' }
];

const DropdownsPage = () => {
  const [dropdownValues, setDropdownValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ value: '', label: '', description: '', is_active: true, sort_order: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchDropdowns();
  }, []);

  const fetchDropdowns = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/dropdowns`);
      setDropdownValues(response.data.dropdowns || {});
    } catch (error) {
      console.error('Failed to fetch dropdowns:', error);
      // Initialize with empty arrays for each category
      const emptyDropdowns = {};
      DROPDOWN_CATEGORIES.forEach(cat => {
        emptyDropdowns[cat.id] = [];
      });
      setDropdownValues(emptyDropdowns);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  const openDialog = (category, item = null) => {
    setCurrentCategory(category);
    if (item) {
      setEditingItem(item);
      setFormData({
        value: item.value,
        label: item.label || item.value,
        description: item.description || '',
        is_active: item.is_active !== false,
        sort_order: item.sort_order || 0
      });
    } else {
      setEditingItem(null);
      setFormData({ value: '', label: '', description: '', is_active: true, sort_order: 0 });
    }
    setDialogOpen(true);
  };

  const saveItem = async () => {
    if (!formData.value.trim()) {
      toast.error('Value is required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        label: formData.label || formData.value,
        category: currentCategory.id
      };
      
      if (editingItem) {
        await axios.put(`${API}/admin/dropdowns/${currentCategory.id}/${editingItem.id}`, payload);
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/admin/dropdowns/${currentCategory.id}`, payload);
        toast.success('Created successfully');
      }
      setDialogOpen(false);
      fetchDropdowns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (categoryId, itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${API}/admin/dropdowns/${categoryId}/${itemId}`);
      toast.success('Deleted successfully');
      fetchDropdowns();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dropdowns-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <List className="w-6 h-6" />
              Dropdowns & Lookups
            </h1>
            <p className="text-muted-foreground">Manage all dropdown values and lookup lists</p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {DROPDOWN_CATEGORIES.map((category) => {
          const items = dropdownValues[category.id] || [];
          const isExpanded = expandedCategory === category.id;
          
          return (
            <Card key={category.id} className="overflow-hidden">
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => toggleCategory(category.id)}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  )}
                  <div>
                    <h3 className="font-medium">{category.name}</h3>
                    <p className="text-sm text-muted-foreground">{category.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{items.length} items</Badge>
                  <Button 
                    size="sm" 
                    onClick={(e) => {
                      e.stopPropagation();
                      openDialog(category);
                    }}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {isExpanded && (
                <CardContent className="border-t pt-4">
                  {items.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Value</TableHead>
                          <TableHead>Label</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-mono text-sm">{item.value}</TableCell>
                            <TableCell>{item.label || item.value}</TableCell>
                            <TableCell>
                              <Badge className={item.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                                {item.is_active !== false ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" onClick={() => openDialog(category, item)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteItem(category.id, item.id)} className="text-destructive">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <p>No values defined for this dropdown</p>
                      <Button className="mt-2" variant="outline" size="sm" onClick={() => openDialog(category)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Value
                      </Button>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Edit' : 'Add'} {currentCategory?.name} Value
            </DialogTitle>
            <DialogDescription>Configure the dropdown value</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Value *</Label>
              <Input
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder="Internal value (e.g., employed_full_time)"
              />
              <p className="text-xs text-muted-foreground">This is the stored value, use snake_case</p>
            </div>
            <div className="space-y-2">
              <Label>Display Label</Label>
              <Input
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="Display name (e.g., Employed Full-Time)"
              />
              <p className="text-xs text-muted-foreground">How it appears to users (defaults to value if empty)</p>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Active</p>
                <p className="text-sm text-muted-foreground">Available for selection</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveItem} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DropdownsPage;
